import { useEffect } from 'react';
import { App as AntApp, Button, Form, Input, Modal, Space, Typography } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { Trans, useTranslation } from 'react-i18next';

import { ErrorAlert } from '@/components/ErrorAlert';
import { useBlockOpticMutation } from './hooks';

const { Text } = Typography;

interface BlockOpticModalProps {
  open: boolean;
  tenantId: string | null;
  opticName: string;
  onClose: () => void;
}

interface BlockFormValues {
  reason: string;
}

// Диалог блокировки оптики (M5.7). Причина обязательна — валидируется на форме
// и на бэке (422 при пустой). POST /platform/optics/{id}/block { reason }.
export function BlockOpticModal({
  open,
  tenantId,
  opticName,
  onClose,
}: BlockOpticModalProps): JSX.Element {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const [form] = Form.useForm<BlockFormValues>();
  const mutation = useBlockOpticMutation();

  useEffect(() => {
    if (open) {
      form.resetFields();
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleFinish = (values: BlockFormValues): void => {
    if (!tenantId) return;
    mutation.mutate(
      { tenantId, reason: values.reason.trim() },
      {
        onSuccess: () => {
          void message.success(t('platform.optics.toast_blocked'));
          onClose();
        },
      },
    );
  };

  return (
    <Modal
      title={
        <Space size={10}>
          <span
            style={{
              width: 30,
              height: 30,
              borderRadius: 8,
              background: '#FBE3E8',
              color: '#9C2B3F',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <LockOutlined />
          </span>
          {t('platform.optics.modal_title')}
        </Space>
      }
      open={open}
      onCancel={onClose}
      destroyOnHidden
      footer={
        <Space>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            danger
            type="primary"
            loading={mutation.isPending}
            onClick={() => form.submit()}
            data-testid="block-submit"
          >
            {t('platform.optics.modal_submit')}
          </Button>
        </Space>
      }
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 14 }}>
        <Trans
          i18nKey="platform.optics.modal_description"
          values={{ name: opticName }}
          components={{ b: <Text strong /> }}
        />
      </Text>
      <Form form={form} layout="vertical" onFinish={handleFinish} requiredMark={false}>
        <Form.Item
          name="reason"
          label={t('platform.optics.modal_reason_label')}
          rules={[
            {
              required: true,
              whitespace: true,
              message: t('platform.optics.modal_reason_required'),
            },
          ]}
        >
          <Input.TextArea
            rows={4}
            placeholder={t('platform.optics.modal_reason_placeholder')}
            data-testid="block-reason"
          />
        </Form.Item>
      </Form>
      {mutation.error && <ErrorAlert error={mutation.error} />}
    </Modal>
  );
}
